/**
 * Webview-aware Bluedot Initialization Utility
 *
 * Handles Bluedot SDK initialization for both standalone webapp
 * and webview (iOS/Android) environments.
 */

import { bluedot } from "@bluedot-innovation/javascript-sdk";
import bluedotConfig from "../config/bluedot";

// Environment detection
const isRunningInWebView = () => {
  if (typeof window === "undefined") {
    return false;
  }

  // 1. Explicit flag from our native app (most reliable)
  if (window.isNativeApp === true) return true;

  // 2. React Native WebView bridge (provided by react-native-webview)
  if (window.ReactNativeWebView) return true;

  // 3. Platform specific bridges
  if (window.AndroidInterface) return true;
  if (window.webkit?.messageHandlers?.nativeApp) return true;

  // 4. User Agent checks (Fallback)
  const userAgent = window.navigator.userAgent || "";
  
  // Android WebView usually includes "wv" or "WebView"
  if (userAgent.includes("wv") || userAgent.includes("WebView")) return true;

  // iOS WebView usually includes "Mobile/" but NOT "Safari/" (unless it's SFSafariViewController, which acts like Safari)
  // NOTE: This check can be flaky. If we rely on bridge injection, we might not need this.
  // For now, let's keep it but ensure it's strict.
  if (userAgent.includes("Mobile/") && !userAgent.includes("Safari/")) return true;

  return false;
};

// Enhanced logging
const log = (...args) => {
  if (bluedotConfig.debugMode) {
    console.log("[Bluedot-WebView]", ...args);
  }
};

const logError = (...args) => {
  console.error("[Bluedot-WebView]", ...args);
};

/**
 * Initialize Bluedot SDK with webview-aware configuration
 *
 * @returns {Promise<{success: boolean, error?: string, environment: string}>}
 */
export const initializeBluedotWebViewAware = async () => {
  if (!bluedotConfig.enabled) {
    log("Bluedot is disabled via configuration");
    return {
      success: false,
      error: "Disabled via configuration",
      environment: "standalone"
    };
  }

  if (bluedotConfig.projectId.length === 0) {
    logError("Missing project ID");
    return {
      success: false,
      error: "Missing project ID",
      environment: "standalone"
    };
  }

  const isWebView = isRunningInWebView();
  const environment = isWebView ? "webview" : "standalone";

  log(`Initializing Bluedot SDK for ${environment} environment...`);
  log(`Project ID: ${bluedotConfig.projectId ? "[REDACTED]" : "[MISSING]"}`);

  try {
    // Initialize the SDK
    await bluedot.initialize(bluedotConfig.projectId);
    log("Bluedot SDK initialized successfully");

    // Set debug mode if enabled
    if (bluedotConfig.debugMode) {
      bluedot.config.setDebugModeEnabled(true);
      log("Debug mode enabled");
    }

    // Configure user persistence
    // In webviews, user persistence might behave differently
    if (bluedotConfig.mockMode === false) {
      try {
        bluedot.config.setUserPersistenceEnabled(true);
        log("User persistence enabled");
      } catch (persistenceError) {
        log(`User persistence not available: ${persistenceError.message}`);
      }
    }

    log(`Bluedot initialization complete for ${environment}`);
    return { success: true, environment };
  } catch (error) {
    logError(`Failed to initialize Bluedot: ${error.message}`);
    return { success: false, error: error.message, environment };
  }
};

/**
 * Check if running in a native app webview
 *
 * @returns {boolean}
 */
export const isNativeAppWebView = () => {
  return isRunningInWebView();
};

/**
 * Get environment information for debugging
 *
 * @returns {Object}
 */
export const getBluedotEnvironmentInfo = () => {
  if (typeof window === "undefined") {
    return { environment: "SSR", isWebView: false };
  }

  const userAgent = window.navigator.userAgent || "";
  const isWebView = isRunningInWebView();

  return {
    environment: isWebView ? "webview" : "standalone",
    userAgent: bluedotConfig.debugMode ? userAgent : "[HIDDEN]",
    indicators: {
      isNativeApp: window.isNativeApp === true,
      hasReactNativeWebView: !!window.ReactNativeWebView,
      hasAndroidInterface: !!window.AndroidInterface,
      hasWebkitBridge: !!(window.webkit?.messageHandlers?.nativeApp),
      uaHasWv: userAgent.includes("wv") || userAgent.includes("WebView"),
      uaIsIOSWebView: userAgent.includes("Mobile/") && !userAgent.includes("Safari/")
    },
    config: bluedotConfig.toDebugString()
  };
};

export default {
  initializeBluedotWebViewAware,
  isNativeAppWebView,
  getBluedotEnvironmentInfo
};
